import React, { useState, useEffect, useMemo } from "react";
import DocumentTitle from "react-document-title";
import { FaAngleRight, FaPencilAlt, FaPlus } from "react-icons/fa";
import { FaSpinner, FaTrash, FaXmark } from "react-icons/fa6";

import SectionTable, { SectionHead, SectionBody } from "styles/components/SectionTable";
import { getAccessToken } from "utilities/localStore";

import styled from "styled-components";
import { useDispatch } from "react-redux";
import { addNotification, openModal } from "store/state/ui/actions";
import { MODAL_CONFIRMATION } from "lib/constants";
import { createNotification, LEVELS } from "utilities/notification";
import { restApiBaseUrl } from "constants/api";

const apiUrl = restApiBaseUrl;

const NavigatorContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  cursor: default;
  position: relative;
`;

const NavigatorAction = styled.div`
  position: absolute;
  top: -32px;
  left: 0px;
  z-index: 10;
  font-weight: normal;
  font-size: 12px;
  padding: 3px 8px;
  padding-right: 9px;
  padding-bottom: 4px;
  border-radius: 3px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  background-color: #484848;
  color: #fff;

  @media screen and (max-width: 1265px) {
    margin-left: 0px;
  }

  margin-left: calc((100vw - 1265px) / 2);

  :hover {
    background-color: #333;
  }

  svg {
    margin-bottom: -1px;
    font-weight: normal;
  }
`;

const LoadingIndicator = styled(NavigatorAction)`
  left: 125px;
  gap: 8px;
  background-color: transparent;
  color: #333;
  pointer-events: none;

  svg {
    animation: spin 1s linear infinite;
  }
`;

const NavigatorColumn = styled.div`
  width: 100%;
  max-width: 350px;
  height: 100%;
  border-top: 1px solid #b8c2c7;
  border-bottom: 1px solid #b8c2c7;
  border-left: 1px solid #b8c2c7;
  padding-bottom: 50px;
  text-align: left;
  padding-top: 12px;
  padding-bottom: 12px;
  position: relative;
  contain: strict;

  :first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }

  :last-child {
    border-right: 1px solid #b8c2c7;
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const NavigatorColumnTitle = styled.div`
  position: sticky;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  padding: 0px 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #b8c2c7;
`;

const NavigatorColumnContent = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  contain: strict;
`;

const NavigatorGroup = styled.div`
  padding: 8px 10px;

  :last-child {
    margin-bottom: 75px;
  }
`;

const NavigatorGroupName = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #484848;
  margin-bottom: 6px;
  padding-left: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavigatorGroupAction = styled.div`
  font-weight: normal;
  font-size: 12px;
  margin-left: 8px;
  color: #484848;
  padding: 6px;
  border-radius: 2px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    margin-bottom: -1px;
    font-weight: normal;
  }

  :hover {
    background-color: #484848;
    color: #fff;
  }
`;

const NavigatorChild = styled.div`
  font-size: 14px;
  color: #696969;
  margin-bottom: 6px;
  padding: 8px 12px;
  border-radius: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  :hover {
    background-color: #484848;
    color: #fff;
  }

  ${(props) =>
    props.isDisabled &&
    `
    pointer-events: none;
    font-weight: normal;
    `}

${(props) =>
  props.isActive &&
  `
    background-color: #484848;
    color: #fff;
  `}

${(props) =>
  props.disableHover &&
  `
    :hover {
      background-color: transparent;
      color: #696969;
    }
  `}
`;

const SubjectForm = styled.form`
  display: block;
  display: flex;
  align-items: center;
  width: 90%;
  margin-left: 10px;
  margin-bottom: 10px;
  gap: 2px;
`;

const SubjectFormInput = styled.input`
  display: block;
  width: 100%;
  outline: none;
  border: 1px solid #b8c2c7;
  height: fit-content;
  padding: 6px 12px;
  border-radius: 2px;
  box-sizing: border-box;
`;

const SubjectFormButton = styled.button`
  display: block;
  outline: none;
  border: none;
  height: fit-content;
  padding: 8px 12px;
  border-radius: 2px;
  background-color: #484848;
  color: #fff;
  font-size: 12px;

  :hover {
    background-color: #333;
  }

  :disabled {
    pointer-events: none;
    opacity: 0.7;
  }
`;

const Form = styled.form`
  display: block;
  display: flex;
  align-items: center;
  width: 90%;
  gap: 2px;
`;

const FormInput = styled.input`
  display: block;
  width: 100%;
  outline: none;
  border: 1px solid #b8c2c7;
  padding: 4px 8px;
  font-size: 14px;
  border-radius: 2px;
  box-sizing: border-box;
`;

const FormButton = styled.button`
  display: block;
  outline: none;
  border: none;
  padding: 6px 8px;
  border-radius: 2px;
  background-color: #484848;
  color: #fff;
  font-size: 12px;

  :hover {
    background-color: #333;
  }

  :disabled {
    pointer-events: none;
    opacity: 0.7;
  }
`;

const AdminPage = () => {
  const dispatch = useDispatch();

  const [tags, setTags] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [tagPath, setTagPath] = useState([]);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftTag, setDraftTag] = useState("");
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const [tagForm, setTagForm] = useState(null);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editingTagId, setEditingTagId] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState("");

  const tagsByDepth = useMemo(() => {
    return tags.reduce((depthMap, tag) => {
      let depthList = depthMap[tag.depth] || [];

      depthMap[tag.depth] = depthList.concat(tag);

      return depthMap;
    }, {});
  }, [tags]);

  // Fetch accounts on mount
  useEffect(() => {
    setLoadingMessage("Fetching subjects and tags...");

    const tagsUrl = `${apiUrl}/rest/tags`;
    fetch(tagsUrl, {
      mode: "cors",
      method: "get",
      headers: {
        Authorization: getAccessToken(),
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setTags(result.data);
        setLoadingMessage("");
      });

    const subjectsUrl = `${apiUrl}/rest/subjects`;
    fetch(subjectsUrl, {
      mode: "cors",
      method: "get",
      headers: {
        Authorization: getAccessToken(),
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setSubjects(result.data.filter((s) => s.discipline_id === "1"));
      });
  }, []);

  const resetForms = () => {
    setDraftSubject("");
    setDraftTag("");
    setShowSubjectForm(false);
    setTagForm(null);
    setEditingSubjectId(null);
    setEditingTagId(null);
  };

  const updateTagPath = (newPath) => {
    resetForms();
    setTagPath(newPath);
  };

  const createDraftSubject = () => {
    resetForms();
    setShowSubjectForm(true);
  };

  const editSubject = (subjectId) => {
    resetForms();
    setDraftSubject(subjects.find((s) => s.id === subjectId).name);
    setEditingSubjectId(subjectId);
  };

  const editTag = (tagId) => {
    resetForms();
    setDraftTag(tags.find((t) => t.id === tagId).name);
    setEditingTagId(tagId);
  };

  const createDraftTag = ({ subjectId, depth }) => {
    resetForms();
    setTagForm({
      subjectId,
      depth,
    });
  };

  const deleteSubject = (subjectId) => {
    dispatch(
      openModal({
        type: MODAL_CONFIRMATION,
        data: {
          id: subjectId,
          title: subjects.find((s) => s.id === subjectId)?.name || "this subject",
          icon: "ico-unit-delete.svg",
          type: "delete",
          action: () => {
            setLoadingMessage("Deleting subject...");

            const url = `${apiUrl}/rest/subjects/${subjectId}`;
            fetch(url, {
              mode: "cors",
              method: "delete",
              headers: {
                Authorization: getAccessToken(),
                "Content-Type": "application/json",
              },
            })
              .then((response) => {
                return response.json();
              })
              .then((result) => {
                console.log("[delete][subject]", { result });

                if (result.status === "error") {
                  dispatch(
                    addNotification({
                      notification: createNotification(
                        LEVELS.ERROR,
                        "Delete Subject",
                        result.message || "System Error"
                      ),
                    })
                  );
                } else {
                  dispatch(
                    addNotification({
                      notification: createNotification(
                        LEVELS.SUCCESS,
                        "Delete Subject",
                        result.message || "Success"
                      ),
                    })
                  );
                  setSubjects((previous) => previous.filter((p) => p.id !== subjectId));
                }
                setLoadingMessage("");
                resetForms();
              });
          },
        },
      })
    );
  };

  const deleteTag = (tagId) => {
    dispatch(
      openModal({
        type: MODAL_CONFIRMATION,
        data: {
          id: tagId,
          title: tags.find((t) => t.id === tagId)?.name || "this tag",
          type: "delete",
          action: () => {
            setLoadingMessage("Deleting tag...");

            const url = `${apiUrl}/rest/tags/${tagId}`;
            fetch(url, {
              mode: "cors",
              method: "delete",
              headers: {
                Authorization: getAccessToken(),
                "Content-Type": "application/json",
              },
            })
              .then((response) => {
                return response.json();
              })
              .then((result) => {
                console.log("[delete][tag]", { result });

                if (result.status === "error") {
                  dispatch(
                    addNotification({
                      notification: createNotification(
                        LEVELS.ERROR,
                        "Delete Tag",
                        result.message || "System Error"
                      ),
                    })
                  );
                } else {
                  dispatch(
                    addNotification({
                      notification: createNotification(
                        LEVELS.SUCCESS,
                        "Delete Tag",
                        result.message || "Success"
                      ),
                    })
                  );
                  setTags((previous) => previous.filter((p) => p.id !== tagId));
                }

                setLoadingMessage("");
                resetForms();
              });
          },
        },
      })
    );
  };

  return (
    <DocumentTitle title="Tag Manager | Stemify">
      <SectionTable
        style={{
          padding: "10px",
          height: "calc(100vh - 125px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SectionHead style={{ marginBottom: "10px", overflow: "visible" }}>
          <h2 style={{ textAlign: "center" }}>Tag Manager</h2>
        </SectionHead>

        <SectionBody style={{ padding: "50px 100px", height: "100%", flexGrow: 1 }}>
          <NavigatorContainer>
            <NavigatorAction
              onClick={() => {
                createDraftSubject();
              }}
            >
              <FaPlus size={10} />
              <span>Add Subject</span>
            </NavigatorAction>
            {loadingMessage && (
              <LoadingIndicator>
                <FaSpinner className="fa-spin" />
                <span>{loadingMessage}</span>
              </LoadingIndicator>
            )}

            <NavigatorColumn>
              <NavigatorColumnTitle>Depth 1 Tags by Subject</NavigatorColumnTitle>

              <NavigatorColumnContent>
                {showSubjectForm && (
                  <SubjectForm
                    onSubmit={(event) => {
                      event.preventDefault();
                      setLoadingMessage("Creating subject...");

                      const url = `${apiUrl}/rest/subjects`;
                      fetch(url, {
                        mode: "cors",
                        method: "post",
                        headers: {
                          Authorization: getAccessToken(),
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          name: draftSubject,
                        }),
                      })
                        .then((response) => response.json())
                        .then((result) => {
                          setSubjects((previous) => previous.concat(result.data));
                          setLoadingMessage("");
                          resetForms();
                        });
                    }}
                  >
                    <SubjectFormInput
                      type="text"
                      autoFocus
                      placeholder="Subject name..."
                      value={draftSubject}
                      onChange={(event) => {
                        setDraftSubject(event.target.value);
                      }}
                    />
                    <SubjectFormButton disabled={draftSubject.length === 0}>
                      Create
                    </SubjectFormButton>
                  </SubjectForm>
                )}

                {subjects.map((subject) => {
                  return (
                    <NavigatorGroup key={subject.id}>
                      <NavigatorGroupName>
                        {editingSubjectId === subject.id ? (
                          <Form
                            onSubmit={(event) => {
                              event.preventDefault();
                              setLoadingMessage("Updating subject...");

                              const url = `${apiUrl}/rest/subjects/${editingSubjectId}`;
                              fetch(url, {
                                mode: "cors",
                                method: "put",
                                headers: {
                                  Authorization: getAccessToken(),
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  name: draftSubject,
                                }),
                              })
                                .then((response) => response.json())
                                .then((result) => {
                                  setSubjects((previous) =>
                                    previous.map((s) => {
                                      if (s.id === editingSubjectId) {
                                        return {
                                          ...s,
                                          name: result.data.name,
                                        };
                                      }

                                      return s;
                                    })
                                  );
                                  setLoadingMessage("");
                                  resetForms();
                                });
                            }}
                          >
                            <FormInput
                              type="text"
                              autoFocus
                              value={draftSubject}
                              onChange={(event) => {
                                setDraftSubject(event.target.value);
                              }}
                            />
                            <FormButton disabled={draftSubject.length === 0}>Update</FormButton>
                            <FormButton onClick={resetForms}>
                              <FaXmark size={10} />
                            </FormButton>
                          </Form>
                        ) : (
                          <>
                            <span>{subject.name}</span>

                            <div style={{ display: "flex", alignItems: "center" }}>
                              <NavigatorGroupAction
                                title="Edit Subject"
                                onClick={() => {
                                  editSubject(subject.id);
                                }}
                              >
                                <FaPencilAlt size={10} />
                              </NavigatorGroupAction>
                              {tagsByDepth[1]?.filter((tag) => tag.subject_id === subject.id)
                                .length >= 0 && (
                                <NavigatorGroupAction
                                  title="Delete Subject"
                                  onClick={() => {
                                    deleteSubject(subject.id);
                                  }}
                                >
                                  <FaTrash size={10} />
                                </NavigatorGroupAction>
                              )}

                              <NavigatorGroupAction
                                title="Add Tag"
                                onClick={() => {
                                  createDraftTag({
                                    depth: 1,
                                    subjectId: subject.id,
                                  });
                                }}
                              >
                                <FaPlus size={10} />
                              </NavigatorGroupAction>
                            </div>
                          </>
                        )}
                      </NavigatorGroupName>
                      <div>
                        {tagsByDepth[1]
                          ?.filter((tag) => tag.subject_id === subject.id)
                          .map((tag) => {
                            return (
                              <NavigatorChild
                                key={tag.id}
                                isActive={tagPath[0] === tag.id}
                                onClick={() => {
                                  updateTagPath([tag.id]);
                                }}
                              >
                                <span>{tag.name}</span>
                                <FaAngleRight />
                              </NavigatorChild>
                            );
                          })}
                        {tagsByDepth[1]?.filter((tag) => tag.subject_id === subject.id).length ===
                          0 && (
                          <NavigatorChild isDisabled={true}>
                            No tags for this subject.
                          </NavigatorChild>
                        )}
                        {tagForm?.subjectId === subject.id && tagForm?.depth === 1 ? (
                          <Form
                            onSubmit={(event) => {
                              event.preventDefault();
                              setLoadingMessage("Creating tag...");

                              const url = `${apiUrl}/rest/tags`;
                              fetch(url, {
                                mode: "cors",
                                method: "post",
                                headers: {
                                  Authorization: getAccessToken(),
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  name: draftTag,
                                  depth: tagForm.depth,
                                  subjectId: tagForm.subjectId,
                                }),
                              })
                                .then((response) => response.json())
                                .then((result) => {
                                  setTags((previous) => previous.concat(result.data));
                                  setLoadingMessage("");
                                  resetForms();
                                });
                            }}
                          >
                            <FormInput
                              type="text"
                              autoFocus
                              placeholder={`${subject.name} tag...`}
                              value={draftTag}
                              onChange={(event) => {
                                setDraftTag(event.target.value);
                              }}
                            />
                            <FormButton disabled={draftTag.length === 0}>Create</FormButton>
                          </Form>
                        ) : null}
                      </div>
                    </NavigatorGroup>
                  );
                })}
              </NavigatorColumnContent>
            </NavigatorColumn>

            <NavigatorColumn>
              <NavigatorColumnTitle>Depth 2 Tags for Depth 1 Tag Selected</NavigatorColumnTitle>

              <NavigatorColumnContent>
                <NavigatorGroup>
                  <NavigatorGroupName>
                    {tagPath.length > 0 ? (
                      <>
                        {editingTagId === tagPath[0] ? (
                          <Form
                            onSubmit={(event) => {
                              event.preventDefault();
                              setLoadingMessage("Updating tag...");

                              const url = `${apiUrl}/rest/tags/${editingTagId}`;
                              fetch(url, {
                                mode: "cors",
                                method: "put",
                                headers: {
                                  Authorization: getAccessToken(),
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  name: draftTag,
                                }),
                              })
                                .then((response) => response.json())
                                .then((result) => {
                                  setTags((previous) =>
                                    previous.map((t) => {
                                      if (t.id === editingTagId) {
                                        return {
                                          ...t,
                                          name: result.data.name,
                                        };
                                      }

                                      return t;
                                    })
                                  );
                                  setLoadingMessage("");
                                  resetForms();
                                });
                            }}
                          >
                            <FormInput
                              type="text"
                              autoFocus
                              value={draftTag}
                              onChange={(event) => {
                                setDraftTag(event.target.value);
                              }}
                            />
                            <FormButton disabled={draftTag.length === 0}>Update</FormButton>
                            <FormButton onClick={resetForms}>
                              <FaXmark size={10} />
                            </FormButton>
                          </Form>
                        ) : (
                          <>
                            <span>{tagsByDepth[1]?.find((t) => t.id === tagPath[0]).name}</span>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <NavigatorGroupAction
                                title="Edit Tag"
                                onClick={() => {
                                  editTag(tagsByDepth[1]?.find((t) => t.id === tagPath[0]).id);
                                }}
                              >
                                <FaPencilAlt size={10} />
                              </NavigatorGroupAction>
                              {tagPath.length > 0 &&
                                tagsByDepth[2]?.filter(
                                  (tag) => tag.tag_path.split(".")[0] === tagPath[0]
                                ).length === 0 && (
                                  <NavigatorGroupAction
                                    title="Delete Tag"
                                    onClick={() => {
                                      deleteTag(
                                        tagsByDepth[1]?.find((t) => t.id === tagPath[0]).id
                                      );
                                    }}
                                  >
                                    <FaTrash size={10} />
                                  </NavigatorGroupAction>
                                )}
                              <NavigatorGroupAction
                                title="Add Tag"
                                onClick={() => {
                                  createDraftTag({
                                    depth: 2,
                                    subjectId: tagsByDepth[1]?.find((t) => t.id === tagPath[0])
                                      .subject_id,
                                  });
                                }}
                              >
                                <FaPlus size={10} />
                              </NavigatorGroupAction>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <NavigatorChild isDisabled={true}>
                        Select a tag from the first column.
                      </NavigatorChild>
                    )}
                  </NavigatorGroupName>

                  {tagPath.length > 0
                    ? tagsByDepth[2]
                        ?.filter((tag) => tag.tag_path.split(".")[0] === tagPath[0])
                        .map((tag) => {
                          return (
                            <NavigatorChild
                              key={tag.id}
                              isActive={tagPath[1] === tag.id}
                              onClick={() => {
                                updateTagPath((previous) => [previous[0], tag.id]);
                              }}
                            >
                              <span>{tag.name}</span>
                              <FaAngleRight />
                            </NavigatorChild>
                          );
                        })
                    : null}
                  {tagPath.length > 0
                    ? tagsByDepth[2]?.filter((tag) => tag.tag_path.split(".")[0] === tagPath[0])
                        .length === 0 && (
                        <NavigatorChild isDisabled={true}>
                          No child tags created for this tag.
                        </NavigatorChild>
                      )
                    : null}

                  {tagForm?.depth === 2 ? (
                    <Form
                      onSubmit={(event) => {
                        event.preventDefault();
                        setLoadingMessage("Creating tag...");

                        const url = `${apiUrl}/rest/tags`;
                        fetch(url, {
                          mode: "cors",
                          method: "post",
                          headers: {
                            Authorization: getAccessToken(),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            name: draftTag,
                            depth: tagForm.depth,
                            subjectId: tagForm.subjectId,
                            tagPath: tagPath[0],
                          }),
                        })
                          .then((response) => response.json())
                          .then((result) => {
                            setTags((previous) => previous.concat(result.data));
                            setLoadingMessage("");
                            resetForms();
                          });
                      }}
                    >
                      <FormInput
                        type="text"
                        autoFocus
                        placeholder={`${
                          tagsByDepth[1]?.find((t) => t.id === tagPath[0]).name
                        } tag...`}
                        value={draftTag}
                        onChange={(event) => {
                          setDraftTag(event.target.value);
                        }}
                      />
                      <FormButton disabled={draftTag.length === 0}>Create</FormButton>
                    </Form>
                  ) : null}
                </NavigatorGroup>
              </NavigatorColumnContent>
            </NavigatorColumn>

            <NavigatorColumn>
              <NavigatorColumnTitle>Depth 3 Tags for Depth 2 Tag Selected</NavigatorColumnTitle>

              <NavigatorColumnContent>
                <NavigatorGroup>
                  <NavigatorGroupName>
                    {tagPath.length > 1 ? (
                      <>
                        {editingTagId === tagPath[1] ? (
                          <Form
                            onSubmit={(event) => {
                              event.preventDefault();
                              setLoadingMessage("Updating tag...");

                              const url = `${apiUrl}/rest/tags/${editingTagId}`;
                              fetch(url, {
                                mode: "cors",
                                method: "put",
                                headers: {
                                  Authorization: getAccessToken(),
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  name: draftTag,
                                }),
                              })
                                .then((response) => response.json())
                                .then((result) => {
                                  setTags((previous) =>
                                    previous.map((t) => {
                                      if (t.id === editingTagId) {
                                        return {
                                          ...t,
                                          name: result.data.name,
                                        };
                                      }

                                      return t;
                                    })
                                  );
                                  setLoadingMessage("");
                                  resetForms();
                                });
                            }}
                          >
                            <FormInput
                              type="text"
                              autoFocus
                              value={draftTag}
                              onChange={(event) => {
                                setDraftTag(event.target.value);
                              }}
                            />
                            <FormButton disabled={draftTag.length === 0}>Update</FormButton>
                            <FormButton onClick={resetForms}>
                              <FaXmark size={10} />
                            </FormButton>
                          </Form>
                        ) : (
                          <>
                            <span>{tagsByDepth[2]?.find((t) => t.id === tagPath[1]).name}</span>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <NavigatorGroupAction
                                title="Edit Tag"
                                onClick={() => {
                                  editTag(tagsByDepth[2]?.find((t) => t.id === tagPath[1]).id);
                                }}
                              >
                                <FaPencilAlt size={10} />
                              </NavigatorGroupAction>
                              <NavigatorGroupAction
                                title="Add Tag"
                                onClick={() => {
                                  createDraftTag({
                                    depth: 3,
                                    subjectId: tagsByDepth[2]?.find((t) => t.id === tagPath[1])
                                      .subject_id,
                                  });
                                }}
                              >
                                <FaPlus size={10} />
                              </NavigatorGroupAction>
                            </div>
                          </>
                        )}
                      </>
                    ) : null}
                  </NavigatorGroupName>

                  {tagPath.length > 1 ? (
                    tagsByDepth[3]
                      ?.filter((tag) => tag.tag_path.split(".")[1] === tagPath[1])
                      .map((tag) => {
                        if (editingTagId === tag.id) {
                          return (
                            <Form
                              key={tag.id}
                              onSubmit={(event) => {
                                event.preventDefault();
                                setLoadingMessage("Updating tag...");

                                const url = `${apiUrl}/rest/tags/${editingTagId}`;
                                fetch(url, {
                                  mode: "cors",
                                  method: "put",
                                  headers: {
                                    Authorization: getAccessToken(),
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify({
                                    name: draftTag,
                                  }),
                                })
                                  .then((response) => response.json())
                                  .then((result) => {
                                    setTags((previous) =>
                                      previous.map((t) => {
                                        if (t.id === editingTagId) {
                                          return {
                                            ...t,
                                            name: result.data.name,
                                          };
                                        }

                                        return t;
                                      })
                                    );
                                    setLoadingMessage("");
                                    resetForms();
                                  });
                              }}
                            >
                              <FormInput
                                type="text"
                                autoFocus
                                value={draftTag}
                                onChange={(event) => {
                                  setDraftTag(event.target.value);
                                }}
                              />
                              <FormButton disabled={draftTag.length === 0}>Update</FormButton>
                              <FormButton onClick={resetForms}>
                                <FaXmark size={10} />
                              </FormButton>
                            </Form>
                          );
                        }

                        return (
                          <NavigatorChild key={tag.id} disableHover={true}>
                            <span>{tag.name}</span>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                width: "100px",
                                marginBottom: "4px",
                              }}
                            >
                              <NavigatorGroupAction
                                title="Edit Tag"
                                onClick={() => {
                                  editTag(tag.id);
                                }}
                              >
                                <FaPencilAlt />
                              </NavigatorGroupAction>

                              <NavigatorGroupAction
                                title="Delete Tag"
                                onClick={() => {
                                  deleteTag(tag.id);
                                }}
                              >
                                <FaTrash size={12} />
                              </NavigatorGroupAction>
                            </div>
                          </NavigatorChild>
                        );
                      })
                  ) : (
                    <NavigatorChild isDisabled={true}>
                      Select a tag from the second column.
                    </NavigatorChild>
                  )}

                  {tagPath.length > 1
                    ? tagsByDepth[3]?.filter((tag) => tag.tag_path.split(".")[1] === tagPath[1])
                        .length === 0 && (
                        <NavigatorChild isDisabled={true}>
                          No child tags created for this tag.
                        </NavigatorChild>
                      )
                    : null}

                  {tagForm?.depth === 3 ? (
                    <Form
                      onSubmit={(event) => {
                        event.preventDefault();
                        setLoadingMessage("Creating tag...");

                        const url = `${apiUrl}/rest/tags`;
                        fetch(url, {
                          mode: "cors",
                          method: "post",
                          headers: {
                            Authorization: getAccessToken(),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({
                            name: draftTag,
                            depth: tagForm.depth,
                            subjectId: tagForm.subjectId,
                            tagPath: `${tagPath[0]}.${tagPath[1]}`,
                          }),
                        })
                          .then((response) => response.json())
                          .then((result) => {
                            setTags((previous) => previous.concat(result.data));
                            setLoadingMessage("");
                            resetForms();
                          });
                      }}
                    >
                      <FormInput
                        type="text"
                        autoFocus
                        placeholder={`${
                          tagsByDepth[2]?.find((t) => t.id === tagPath[1]).name
                        } tag...`}
                        value={draftTag}
                        onChange={(event) => {
                          setDraftTag(event.target.value);
                        }}
                      />
                      <FormButton disabled={draftTag.length === 0}>Create</FormButton>
                    </Form>
                  ) : null}
                </NavigatorGroup>
              </NavigatorColumnContent>
            </NavigatorColumn>
          </NavigatorContainer>
        </SectionBody>
      </SectionTable>
    </DocumentTitle>
  );
};

export default AdminPage;
